import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import { translateLyrics, generateVocabulary } from '../lib/openai';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { FaPlay, FaPause, FaLanguage, FaBook, FaBackward, FaForward, FaChevronDown, FaChevronUp, FaGlobe, FaExpand, FaCompress, FaGripHorizontal, FaRedo } from 'react-icons/fa';

interface SongPlayerProps {
  song: {
    id: string;
    song_url: string;
    song_cover: string;
    creator: string;
    language: 'english' | 'spanish' | 'french';
    song_lyrics: string;
    translation: string | null;
    song_vocabulary: any;
  };
  onClose: () => void;
  autoPlay?: boolean;
}

interface LyricLine {
  text: string;
  translation?: string;
}

const SongPlayer: React.FC<SongPlayerProps> = ({ song, onClose, autoPlay = false }) => {
  const [playing, setPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentLyrics, setCurrentLyrics] = useState<LyricLine[]>([]);
  const [activeLyricIndex, setActiveLyricIndex] = useState(-1);
  const [showTranslation, setShowTranslation] = useState(true);
  const [showVocabulary, setShowVocabulary] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState<'english' | 'french' | 'haitian creole'>('english');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [ready, setReady] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [loop, setLoop] = useState(false);
  const [mobileMinimized, setMobileMinimized] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ touchX: number; touchY: number; elX: number; elY: number } | null>(null);
  const mobilePlayerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReactPlayer>(null);
  const lyricsContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lines = song.song_lyrics.split('\n').filter(line => line.trim());
    const formattedLines = lines.map(line => ({
      text: line,
      translation: undefined
    }));
    setCurrentLyrics(formattedLines);

    // Add click outside listener for dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [song]);

  // Clear all translations when target language changes
  useEffect(() => {
    setCurrentLyrics(lyrics => 
      lyrics.map(line => ({
        ...line,
        translation: undefined
      }))
    );
  }, [targetLanguage]);

  const handleTranslate = async (text: string, index: number) => {
    if (translating) return;
    setTranslating(true);

    try {
      const translation = await translateLyrics(text, targetLanguage);
      if (translation) {
        setCurrentLyrics(lyrics => 
          lyrics.map((line, i) => 
            i === index ? { ...line, translation } : line
          )
        );
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to translate lyrics');
    } finally {
      setTranslating(false);
    }
  };

  const handleTranslateAll = async () => {
    if (translating) return;
    setTranslating(true);

    try {
      const translations = await Promise.all(
        currentLyrics.map(async (line) => {
          const translation = await translateLyrics(line.text, targetLanguage);
          return { ...line, translation };
        })
      );
      setCurrentLyrics(translations);
      toast.success('All lyrics translated successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to translate all lyrics');
    } finally {
      setTranslating(false);
    }
  };

  const handleReady = () => {
    setReady(true);
    if (playerRef.current) {
      setDuration(playerRef.current.getDuration());
    }
  };

  const handlePlay = () => {
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const handleEnded = () => {
    if (loop) {
      setProgress(0);
      if (playerRef.current) {
        playerRef.current.seekTo(0);
      }
      setPlaying(true);
    } else {
      setPlaying(false);
      setProgress(0);
      if (playerRef.current) {
        playerRef.current.seekTo(0);
      }
    }
  };

  const handleError = (error: any) => {
    console.error('Player error:', error);
    toast.error('Error playing audio');
    setPlaying(false);
  };

  const handleProgress = ({ playedSeconds }: { playedSeconds: number }) => {
    setProgress(playedSeconds);
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !duration) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    if (playerRef.current) {
      playerRef.current.seekTo(newTime);
    }
    setProgress(newTime);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSkip = (seconds: number) => {
    if (!playerRef.current) return;
    
    const currentTime = playerRef.current.getCurrentTime();
    const newTime = Math.max(0, Math.min(currentTime + seconds, duration));
    playerRef.current.seekTo(newTime);
    setProgress(newTime);
  };

  // Mobile player drag handlers
  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    const el = mobilePlayerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { touchX: clientX, touchY: clientY, elX: rect.left, elY: rect.top };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: TouchEvent | MouseEvent) => {
      if (!dragStartRef.current || !mobilePlayerRef.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const dx = clientX - dragStartRef.current.touchX;
      const dy = clientY - dragStartRef.current.touchY;
      const newX = dragStartRef.current.elX + dx;
      const newY = dragStartRef.current.elY + dy;
      const elW = mobilePlayerRef.current.offsetWidth;
      const elH = mobilePlayerRef.current.offsetHeight;
      const clampedX = Math.max(0, Math.min(newX, window.innerWidth - elW));
      const clampedY = Math.max(0, Math.min(newY, window.innerHeight - elH));
      setDragPosition({ x: clampedX, y: clampedY });
    };

    const handleEnd = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    return () => {
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
    };
  }, [isDragging]);

  // Reset drag position when switching between minimized and expanded
  const handleResetPosition = () => {
    setDragPosition(null);
  };

  return (
    <div className={`min-h-screen bg-gray-900 flex flex-col ${focusMode ? 'pt-0' : ''} relative`}>
      {/* Song Cover Section - Hidden in Focus Mode */}
      {!focusMode && (
        <div className="relative w-full" style={{ maxHeight: '40vh' }}>
          <img
            src={song.song_cover}
            alt={song.creator}
            className="w-full h-full object-cover"
            style={{ maxHeight: '40vh' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900 flex items-end justify-between p-4">
            <div className="text-white">
              <h1 className="text-2xl font-bold">{song.creator}</h1>
              <p className="text-sm opacity-80">{song.language}</p>
            </div>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
                  showTranslation 
                    ? 'bg-yellow-400 text-black' 
                    : 'bg-black/50 text-yellow-400 backdrop-blur-lg'
                } transition-colors duration-200`}
              >
                <FaGlobe className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {targetLanguage.charAt(0).toUpperCase() + targetLanguage.slice(1)}
                </span>
                <FaChevronDown className="w-3 h-3" />
              </button>

              {showLanguageDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-black/90 backdrop-blur-lg rounded-lg shadow-lg py-1 z-50">
                  <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-800">
                    Translate to:
                  </div>
                  {['english', 'french', 'haitian creole'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setTargetLanguage(lang as 'english' | 'french' | 'haitian creole');
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm ${
                        targetLanguage === lang
                          ? 'text-yellow-400 bg-gray-800'
                          : 'text-white hover:bg-gray-800'
                      } transition-colors duration-200`}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                  <div className="border-t border-gray-800 mt-1">
                    <button
                      onClick={() => {
                        handleTranslateAll();
                        setShowLanguageDropdown(false);
                      }}
                      disabled={translating}
                      className="w-full px-4 py-2 text-left text-sm text-yellow-400 hover:bg-gray-800 disabled:opacity-50 transition-colors duration-200"
                    >
                      {translating ? 'Translating...' : 'Translate All'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lyrics Container */}
      <div className={`flex-1 overflow-hidden px-4 ${focusMode ? 'bg-black' : ''}`}>
        <div 
          ref={lyricsContainerRef}
          className="h-full overflow-y-auto py-6 space-y-4 lyrics-container pb-20 md:pb-6"
          style={{ 
            height: focusMode ? '100vh' : 'calc(100vh - 40vh - 120px)',
            scrollBehavior: 'smooth',
            paddingBottom: window.innerWidth < 768 ? '100px' : '80px'
          }}
        >
          {currentLyrics.map((line, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg transition-all duration-300 ${
                index === activeLyricIndex
                  ? 'bg-black scale-105'
                  : 'hover:bg-gray-800'
              }`}
            >
              <p className={`text-lg md:text-xl ${
                index === activeLyricIndex ? 'text-yellow-400 font-medium' : 'text-white'
              }`}>
                {line.text}
              </p>
              {showTranslation && (
                line.translation ? (
                  <p className="text-sm text-yellow-200 mt-1">{line.translation}</p>
                ) : (
                  <button
                    onClick={() => handleTranslate(line.text, index)}
                    disabled={translating}
                    className="mt-2 text-sm text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                  >
                    {translating ? 'Translating...' : `Translate to ${targetLanguage}`}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Player Bar */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0 z-50">
        {/* Glow effect */}
        <div className="absolute -top-4 left-0 right-0 h-8 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        <div className="bg-black/95 backdrop-blur-xl border-t border-yellow-400/20">
          {/* Progress Bar */}
          <div 
            ref={progressBarRef}
            className="relative h-1 bg-white/10 cursor-pointer group"
            onClick={handleProgressBarClick}
          >
            {/* Track fill */}
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-yellow-400 to-yellow-500 transition-all duration-100"
              style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
            />
            {/* Hover indicator */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-yellow-400 rounded-full shadow-lg shadow-yellow-400/50 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${duration ? (progress / duration) * 100 : 0}% - 6px)` }}
            />
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between px-5 py-1.5">
            {/* Left: Song info */}
            <div className="flex items-center gap-2.5 min-w-0 w-1/4">
              <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 ring-1 ring-white/10">
                <img src={song.song_cover} alt={song.creator} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-white truncate">{song.creator}</p>
                <p className="text-[10px] text-gray-400 truncate">{song.language}</p>
              </div>
            </div>

            {/* Center: Playback controls */}
            <div className="flex items-center gap-5 w-2/4 justify-center">
              <button
                onClick={() => handleSkip(-10)}
                className="text-gray-400 hover:text-white transition-colors"
                title="Rewind 10s"
              >
                <FaBackward className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => setPlaying(!playing)}
                disabled={!ready}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-300 text-black transition-all duration-200 hover:scale-105 disabled:opacity-50"
              >
                {playing ? (
                  <FaPause className="w-3.5 h-3.5" />
                ) : (
                  <FaPlay className="w-3.5 h-3.5 ml-0.5" />
                )}
              </button>

              <button
                onClick={() => handleSkip(10)}
                className="text-gray-400 hover:text-white transition-colors"
                title="Forward 10s"
              >
                <FaForward className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-center gap-1.5 text-[10px] text-gray-400 ml-1">
                <span className="tabular-nums">{formatTime(progress)}</span>
                <span>/</span>
                <span className="tabular-nums">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Right: Loop button */}
            <div className="w-1/4 flex justify-end">
              <button
                onClick={() => setLoop(!loop)}
                className={`p-1.5 rounded-full transition-colors ${loop ? 'text-yellow-400 bg-yellow-400/15' : 'text-gray-500 hover:text-gray-300'}`}
                title={loop ? 'Disable loop' : 'Enable loop'}
              >
                <FaRedo className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Player Bar */}
      <div
        ref={mobilePlayerRef}
        className={`md:hidden fixed z-50 ${isDragging ? '' : 'transition-all duration-300'}`}
        style={
          dragPosition
            ? { left: `${dragPosition.x}px`, top: `${dragPosition.y}px`, width: mobileMinimized ? '200px' : 'calc(100% - 24px)' }
            : { left: '12px', right: '12px', bottom: mobileMinimized ? '64px' : '56px' }
        }
      >
        <div className="bg-black/95 backdrop-blur-xl rounded-2xl border border-yellow-400/20 shadow-2xl shadow-black/60 overflow-hidden">
          {/* Drag handle */}
          <div
            onTouchStart={handleDragStart}
            onMouseDown={handleDragStart}
            className="flex items-center justify-center py-1 cursor-grab active:cursor-grabbing touch-none select-none"
          >
            <FaGripHorizontal className="w-4 h-4 text-gray-500" />
          </div>

          {/* Progress bar (always visible) */}
          <div 
            className="relative h-1 bg-white/10 cursor-pointer"
            onClick={handleProgressBarClick}
          >
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-yellow-400 to-yellow-500 transition-all duration-100"
              style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
            />
          </div>

          {mobileMinimized ? (
            /* Minimized: compact single row */
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-white/10">
                <img src={song.song_cover} alt={song.creator} className="w-full h-full object-cover" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{song.creator}</p>
              </div>

              <button
                onClick={() => setPlaying(!playing)}
                disabled={!ready}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-400 text-black disabled:opacity-50"
              >
                {playing ? <FaPause className="w-3 h-3" /> : <FaPlay className="w-3 h-3 ml-0.5" />}
              </button>

              <button
                onClick={() => { setMobileMinimized(false); handleResetPosition(); }}
                className="p-1.5 text-gray-400 hover:text-white transition-colors"
                title="Expand player"
              >
                <FaChevronUp className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            /* Expanded: full controls */
            <div>
              <div className="flex items-center gap-3 px-3 pb-3 pt-1">
                <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 ring-1 ring-white/10">
                  <img src={song.song_cover} alt={song.creator} className="w-full h-full object-cover" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{song.creator}</p>
                  <p className="text-xs text-gray-400">{formatTime(progress)} / {formatTime(duration)}</p>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleSkip(-10)}
                    className="p-1.5 text-gray-400 hover:text-white transition-colors"
                  >
                    <FaBackward className="w-3 h-3" />
                  </button>

                  <button
                    onClick={() => setPlaying(!playing)}
                    disabled={!ready}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-300 text-black transition-all disabled:opacity-50"
                  >
                    {playing ? <FaPause className="w-3.5 h-3.5" /> : <FaPlay className="w-3.5 h-3.5 ml-0.5" />}
                  </button>

                  <button
                    onClick={() => handleSkip(10)}
                    className="p-1.5 text-gray-400 hover:text-white transition-colors"
                  >
                    <FaForward className="w-3 h-3" />
                  </button>

                  {/* Loop button */}
                  <button
                    onClick={() => setLoop(!loop)}
                    className={`p-1.5 rounded-full transition-colors ${loop ? 'text-yellow-400 bg-yellow-400/15' : 'text-gray-500 hover:text-gray-300'}`}
                    title={loop ? 'Disable loop' : 'Enable loop'}
                  >
                    <FaRedo className="w-3 h-3" />
                  </button>
                </div>

                {/* Minimize button */}
                <button
                  onClick={() => setMobileMinimized(true)}
                  className="p-1.5 text-gray-500 hover:text-white transition-colors"
                  title="Minimize player"
                >
                  <FaChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Controls */}
      <div className="fixed top-2 right-2 flex space-x-1">
        <button
          onClick={() => setFocusMode(!focusMode)}
          className={`p-3 rounded-full ${
            focusMode ? 'bg-yellow-400 text-black' : 'bg-black text-yellow-400'
          }`}
          title={focusMode ? 'Exit Focus Mode' : 'Enter Focus Mode'}
        >
          {focusMode ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-3 h-3" />}
        </button>

        <button
          onClick={() => setShowVocabulary(!showVocabulary)}
          className={`p-2 rounded-full ${
            showVocabulary ? 'bg-yellow-400 text-black' : 'bg-black text-yellow-400'
          }`}
        >
          <FaBook className="w-3 h-3" />
        </button>

        <button
          onClick={onClose}
          className="p-1 rounded-full bg-black text-yellow-400"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Single Hidden Audio Player for All Devices */}
      <div className="hidden">
        <ReactPlayer
          ref={playerRef}
          url={song.song_url}
          playing={playing}
          onReady={handleReady}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={handleError}
          onProgress={handleProgress}
          onDuration={setDuration}
          progressInterval={100}
          config={{ file: { forceAudio: true } }}
        />
      </div>
    </div>
  );
};

export default SongPlayer;